import React from "react";
import { Link } from "react-router-dom";
import { Archive, CalendarClock, FileCheck2, Lock, ShieldAlert } from "lucide-react";
import { fmtD } from "@/lib/platform";
import { BATCH_STYLES, Pill } from "@/lib/retention";
import { Panel } from "@/components/rights/RightsBadges";

const Kpi = ({ icon: Icon, label, value, sub, tone = "text-[#003366]", testId, delay }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 rr-rise" style={{ animationDelay: `${delay}ms` }} data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</span><Icon className={`w-4 h-4 ${tone}`} />
    </div>
    <div className={`mt-3 text-3xl font-bold font-mono tracking-tight ${tone}`} data-testid={`${testId}-value`}>{value}</div>
    <div className="text-xs text-slate-500 mt-1">{sub}</div>
  </div>
);

export const RetentionDashboard = ({ d, meta }) => {
  if (!d || !meta) return <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />;
  const holds = Object.values(d.holds_active).reduce((a, b) => a + b, 0);
  const open = ["REVIEW", "PENDING_APPROVAL", "RETURNED", "APPROVED", "EXECUTED"].reduce((a, k) => a + d.batches[k], 0);
  return (
    <div className="space-y-6" data-testid="retention-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi icon={Archive} label="RoPA coverage" value={d.coverage.pct == null ? "—" : `${d.coverage.pct}%`} delay={0} testId="rkpi-coverage"
             sub={`${d.coverage.covered}/${d.coverage.activities} activities have an active rule`} tone={d.coverage.pct >= 80 ? "text-emerald-600" : "text-amber-600"} />
        <Kpi icon={CalendarClock} label="Open dispositions" value={open} delay={60} testId="rkpi-open"
             sub={`${d.overdue_batches} overdue · next run ${fmtD(d.next_scheduled_run)}`} tone={d.overdue_batches ? "text-red-600" : "text-[#003366]"} />
        <Kpi icon={Lock} label="Blocked by holds" value={d.blocked_batches} delay={120} testId="rkpi-blocked" tone={d.blocked_batches ? "text-amber-600" : "text-slate-400"}
             sub={`${holds} active hold(s)`} />
        <Kpi icon={FileCheck2} label="Records disposed" value={d.records_disposed_12m.toLocaleString()} delay={180} testId="rkpi-disposed"
             sub={`last 12 months · ${d.certificates} certificate(s)`} tone="text-[#008080]" />
        <Kpi icon={ShieldAlert} label="Active rules" value={d.rules.ACTIVE} delay={240} testId="rkpi-rules"
             sub={`${d.rules.DRAFT} draft · ${d.rules.PENDING_APPROVAL} awaiting approval`} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <Panel className="xl:col-span-7" eyebrow="Review queue → approve → execute → close" title="Disposition pipeline" testId="retention-pipeline">
          <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
            {Object.entries(meta.batch_statuses).map(([k, l]) => (
              <div key={k} className={`rounded-lg border px-2.5 py-2 ${BATCH_STYLES[k]}`} data-testid={`pipeline-${k.toLowerCase()}`}>
                <div className="text-[10px] font-semibold leading-tight">{l.split(" - ")[0]}</div>
                <div className="text-xl font-mono font-bold">{d.batches[k]}</div>
              </div>
            ))}
          </div>
          <ul className="mt-5 divide-y divide-slate-100">
            {d.queue.length === 0 && <li className="text-xs text-slate-500 py-3">Nothing awaiting action.</li>}
            {d.queue.map((b) => (
              <li key={b.id} className="py-2.5 flex items-center gap-3 text-xs">
                <Link to={`/retention/batches/${b.id}`} className="flex-1 min-w-0 text-[#003366] hover:text-[#008080]" data-testid={`dash-batch-${b.id}`}>
                  <span className="font-mono text-slate-500">{b.batch_id}</span> · {b.rule_name}</Link>
                {b.blocked && <Pill cls="bg-amber-50 text-amber-800 border-amber-200">On hold</Pill>}
                <Pill cls={BATCH_STYLES[b.status]}>{b.status_label}</Pill>
                <span className={`font-mono ${b.is_overdue ? "text-red-600" : "text-slate-500"}`}>due {fmtD(b.due_date)}</span>
              </li>
            ))}
          </ul>
        </Panel>
        <div className="xl:col-span-5 space-y-6">
          <Panel eyebrow="Active rules" title="Rules by trigger" testId="retention-by-trigger">
            {Object.entries(d.by_trigger).map(([k, n]) => (
              <div key={k} className="flex items-center gap-3 py-1.5 text-xs">
                <span className="w-32 text-slate-600">{meta.triggers[k]}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#008080] transition-[width] duration-700" style={{ width: `${Math.min(n * 25, 100)}%` }} /></div>
                <span className="font-mono w-4 text-right">{n}</span>
              </div>
            ))}
          </Panel>
          <Panel eyebrow="Coverage gaps" title="RoPA activities without an active rule" testId="retention-uncovered">
            <ul className="text-xs text-slate-700 space-y-1">{d.coverage.uncovered.map((n, i) => <li key={`${n}-${i}`}>• {n}</li>)}</ul>
          </Panel>
        </div>
      </div>
    </div>
  );
};
